import React, { useEffect } from 'react';
import classnames from 'classnames';
import { AsidePanel, type AsidePanelProps } from '@/components/common/AsidePanel';
import { CloseButton } from '@/components/common/Button';
import { usePanelHost } from '@/core/modules/MapPanelSources';
import styles from './BasePanel.module.scss';

export interface BasePanelProps extends Pick<AsidePanelProps, 'className'> {
  title: string;
  controls?: React.ReactNode;
  controlsClassName?: string;
  animated?: AsidePanelProps['animated'];
  moveMap?: boolean;
  onClickClose?(): void;
  width?: number;
  resizeSeparatorProps?: React.ComponentPropsWithoutRef<'hr'>;
  isResizing?: boolean;
}

export const BasePanel: React.FC<React.PropsWithChildren<BasePanelProps>> = ({
  className,
  title,
  controls,
  controlsClassName,
  animated,
  moveMap,
  onClickClose,
  width,
  resizeSeparatorProps,
  isResizing,
  children,
}) => {
  const host = usePanelHost();

  const panelStyle = width
    ? ({ ['--basePanelWidth' as string]: `${width}px` } as React.CSSProperties)
    : undefined;

  useEffect(() => {
    if (!moveMap) return;
    host.setMapAside(true);
    return () => host.setMapAside(false);
  }, [moveMap, host]);

  return (
    <AsidePanel
      className={classnames(styles.container, className)}
      position="left"
      animated={animated}
      style={panelStyle}
    >
      <div className={styles.titleWrapper}>
        <div className="flex items-center">
          {onClickClose && <CloseButton className={styles.closeBtn} onClick={onClickClose} />}
          <div className={styles.title}>{title}</div>
        </div>
        {controls && <div className={classnames('flex items-center', controlsClassName)}>{controls}</div>}
      </div>
      <div className={styles.body}>{children}</div>
      {resizeSeparatorProps && (
        <div
          {...resizeSeparatorProps}
          className={classnames(styles.resizer, { [styles.resizerActive]: isResizing })}
        />
      )}
    </AsidePanel>
  );
};
